import { Chip, CircularProgress, Icon, IconButton, Tooltip } from "@mui/material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { useVisualizadorArquivos } from "context/VisualizadorArquivosContext";
import PropTypes from "prop-types";
import { useRef, useState } from "react";
import { getFile, post, postUpload } from "utils/WebService";

/**
 * Anexos de uma solicitação de devolução/migração. Todos baixam; o solicitante, com a solicitação em aberto
 * (podeAlterarAnexos), troca um anexo errado pelo correto, exclui ou adiciona. Os arquivos ficam na própria ficha
 * (aba Documentos), ligados à ocorrência da solicitação.
 */
// Azul dos anexos: o "info" do tema some no fundo escuro
const COR_ANEXO = { light: "#1A73E8", dark: "#90CAF9" };
const COR_EXCLUIR = { light: "#C62828", dark: "#EF9A9A" };

export default function AnexosSolicitacao({ solicitacao, onChange, onErro, somenteLeitura = false }) {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;
    const corAnexo = COR_ANEXO[darkMode ? "dark" : "light"];
    const corExcluir = COR_EXCLUIR[darkMode ? "dark" : "light"];
    const { abrirArquivos } = useVisualizadorArquivos();
    const inputRef = useRef(null);
    const substituirRef = useRef(null);
    const [enviando, setEnviando] = useState(false);
    const [confirmandoExclusao, setConfirmandoExclusao] = useState(null);

    const anexos = solicitacao?.anexos || [];
    const podeAlterar = !!solicitacao?.podeAlterarAnexos && !somenteLeitura;
    const avisarErro = (err, padrao) => onErro && onErro(err?.message || padrao);

    const escolherArquivo = (substituirId) => {
        substituirRef.current = substituirId || null;
        if (inputRef.current) {
            inputRef.current.value = "";
            inputRef.current.click();
        }
    };

    const aoEscolher = async (e) => {
        const arquivo = e.target.files && e.target.files[0];
        if (!arquivo) return;
        const formData = new FormData();
        formData.append("file", arquivo);
        if (substituirRef.current) formData.append("substituirId", substituirRef.current);
        setEnviando(true);
        try {
            const atualizada = await postUpload(`/solicitacoes-suporte/${solicitacao.id}/anexos`, formData);
            if (onChange) onChange(atualizada);
        } catch (err) {
            avisarErro(err, "Erro ao anexar o arquivo");
        } finally {
            setEnviando(false);
            substituirRef.current = null;
        }
    };

    const excluir = async (anexo) => {
        setEnviando(true);
        try {
            const atualizada = await post(`/solicitacoes-suporte/${solicitacao.id}/anexos/${anexo.id}/excluir`, {});
            setConfirmandoExclusao(null);
            if (onChange) onChange(atualizada);
        } catch (err) {
            avisarErro(err, "Erro ao excluir o anexo");
        } finally {
            setEnviando(false);
        }
    };

    const baixar = (anexo) => {
        getFile(`/dossie/download-file/${anexo.id}`, anexo.nome || "anexo").catch(err => avisarErro(err, "Erro ao baixar o anexo"));
    };

    // Clique no nome: abre na janela flutuante (sem travar a tela), navegando entre os anexos da solicitação
    const visualizar = (indice) => abrirArquivos(
        anexos.map(a => ({ url: `/dossie/download-file/${a.id}`, nome: a.nome, tipo: a.tipo })), indice);

    if (!podeAlterar && anexos.length === 0) return null;

    return (
        <MDBox>
            <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                <MDTypography variant="caption" fontWeight="bold" color="text">Anexos:</MDTypography>
                {anexos.length === 0 && (
                    <MDTypography variant="caption" color="text" sx={{ opacity: 0.8 }}>nenhum</MDTypography>
                )}
                {anexos.map((a, indice) => (
                    <MDBox key={a.id} display="inline-flex" alignItems="center">
                        <Chip size="small" variant="outlined" icon={<Icon>visibility</Icon>} label={a.nome} clickable
                            onClick={() => visualizar(indice)} title="Clique para visualizar"
                            sx={{ borderColor: corAnexo, color: `${corAnexo} !important`, "& .MuiChip-icon": { color: corAnexo } }} />
                        <Tooltip title="Baixar">
                            <IconButton size="small" onClick={() => baixar(a)} aria-label={`Baixar ${a.nome}`}>
                                <Icon sx={{ color: corAnexo, fontSize: "1.1rem !important" }}>download</Icon>
                            </IconButton>
                        </Tooltip>
                        {podeAlterar && (
                            confirmandoExclusao === a.id ? (
                                <MDBox display="inline-flex" alignItems="center" gap={0.5} ml={0.5}>
                                    <MDTypography variant="caption" color="text">Excluir?</MDTypography>
                                    <MDButton size="small" variant="text" color="dark" onClick={() => setConfirmandoExclusao(null)} disabled={enviando} sx={{ minWidth: 0, px: 1 }}>Não</MDButton>
                                    <MDButton size="small" variant="text" color="error" onClick={() => excluir(a)} disabled={enviando} sx={{ minWidth: 0, px: 1 }}>Sim</MDButton>
                                </MDBox>
                            ) : (
                                <>
                                    <Tooltip title="Trocar pelo arquivo correto">
                                        <span>
                                            <IconButton size="small" onClick={() => escolherArquivo(a.id)} disabled={enviando} aria-label={`Trocar ${a.nome}`}>
                                                <Icon sx={{ color: corAnexo, fontSize: "1.1rem !important" }}>swap_horiz</Icon>
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                    <Tooltip title="Excluir este anexo">
                                        <span>
                                            <IconButton size="small" onClick={() => setConfirmandoExclusao(a.id)} disabled={enviando} aria-label={`Excluir ${a.nome}`}>
                                                <Icon sx={{ color: corExcluir, fontSize: "1.1rem !important" }}>delete_outline</Icon>
                                            </IconButton>
                                        </span>
                                    </Tooltip>
                                </>
                            )
                        )}
                    </MDBox>
                ))}
                {podeAlterar && (
                    <MDButton size="small" variant="text" color="info" onClick={() => escolherArquivo(null)} disabled={enviando}
                        sx={{ px: 1, color: `${corAnexo} !important` }}>
                        <Icon sx={{ mr: 0.5 }}>add</Icon>{anexos.length === 0 ? "Adicionar anexo" : "Adicionar outro"}
                    </MDButton>
                )}
                {enviando && <CircularProgress size={16} color="info" />}
            </MDBox>
            <input ref={inputRef} type="file" hidden onChange={aoEscolher} />
        </MDBox>
    );
}

AnexosSolicitacao.propTypes = {
    solicitacao: PropTypes.object.isRequired,
    onChange: PropTypes.func,
    onErro: PropTypes.func,
    somenteLeitura: PropTypes.bool,
};
